/**
 * Copyright Highway9 Networks Inc.
 */
import moment from "moment";
import React from "react";
import { useLocation } from "react-router-dom";
import { LOCATION_TYPE } from "~/constants";
import { Images } from "./GraphicalResources";

type StringCase = "lower" | "upper" | null;

export const trimSpaces = (str: string, strCase: StringCase = null): string => {
  let trimedString = str.replace(/\s/g, "");
  if (strCase === "lower") {
    trimedString = trimedString.toLowerCase();
  } else if (strCase === "upper") {
    trimedString = trimedString.toUpperCase();
  }
  return trimedString;
};

interface LocationTypeRow {
  location?: {
    locationType?: string;
  };
}

export const generateLocationTypeCell = (row: LocationTypeRow): React.ReactNode => {
  const locationType = row.location?.locationType;
  if (locationType) {
    return (
      <>
        {locationType === LOCATION_TYPE.GCP ? (
          <>
            <img src={Images.GCP} alt="GCP" height="16px" />
            &nbsp;
          </>
        ) : locationType === LOCATION_TYPE.AWS ? (
          <>
            <img src={Images.AWS} alt="AWS" height="16px" />
            &nbsp;
          </>
        ) : locationType === LOCATION_TYPE.AZURE ? (
          <>
            <img src={Images.AZURE} alt="AZURE" height="16px" />
            &nbsp;
          </>
        ) : (
          <>
            <img src={Images.onPrem} alt="onPrem" height="16px" />
            &nbsp;
          </>
        )}
        &nbsp;
        {locationType === LOCATION_TYPE.AWS
          ? LOCATION_TYPE.AWS
          : locationType === LOCATION_TYPE.GCP
            ? LOCATION_TYPE.GCP
            : locationType === LOCATION_TYPE.AZURE
              ? LOCATION_TYPE.AZURE
              : LOCATION_TYPE.PRIVATE_CLOUD}
      </>
    );
  }

  return null;
};

export const useQuery = (): URLSearchParams => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

interface UploadedFileData {
  name: string;
  content: string | ArrayBuffer | null;
}

export const updateExistingArray = (
  e: React.ChangeEvent<HTMLInputElement>,
  _source: unknown,
  existingData: UploadedFileData[] = []
): UploadedFileData[] => {
  const files = e.currentTarget.files;
  if (!files) {
    return existingData;
  }

  Object.keys(files).forEach((i) => {
    const file = files[Number(i)];
    const filename = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result;
      const uploadedFile: UploadedFileData = {
        name: filename,
        content: text,
      };
      console.log("file....", uploadedFile);
      existingData.push(uploadedFile);

      // server call for uploading or reading the files one-by-one
      // by using 'reader.result' or 'uploadedFile'
    };
    reader.readAsBinaryString(file);
  });
  return existingData;
};

export function getDuration(endUnixTime: number, startUnixTime: number): string {
  const startTime = moment.unix(startUnixTime);
  const endTime = moment.unix(endUnixTime);
  const duration = moment.duration(endTime.diff(startTime));

  return duration.humanize();
}

export const truncateString = (str: string | undefined, length: number): string | undefined => {
  if (!str) {
    return undefined;
  }
  if (str?.length > length) {
    return `${str.slice(0, length)} ...`;
  }

  return str;
};

/**
 * Extract clean CSP domain name by removing intermediate segments like "us-10"
 * For example: us-10.demo.highway9networks.com -> demo.highway9networks.com
 */
export function getCleanCspDomain(csp: string): string {
  const domainParts = csp.split(".");

  // Find the first segment that matches an environment pattern (demo, live, track*, test)
  // This skips intermediate segments like "us-10"
  let environmentStartIndex = 0;
  for (let i = 0; i < domainParts.length - 2; i++) {
    const segment = domainParts[i];
    // Known environment patterns: demo, live, track*, test
    if (
      segment === "demo" ||
      segment === "live" ||
      segment.startsWith("track") ||
      segment === "test"
    ) {
      environmentStartIndex = i;
      break;
    }
  }

  // Return everything from the environment segment to the end
  return domainParts.slice(environmentStartIndex).join(".");
}
